import {
    Component,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    effect,
    inject,
    input,
    output,
    signal,
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25TableModule } from "../../../s25-table/s25.table.module";
import { Table } from "../../../s25-table/Table";
import { Budget } from "../Budget";
import { Bind } from "../../../../decorators/bind.decorator";
import { S25PricingUtil } from "../../../s25-pricing/s25.pricing.util";
import { GenericTableActionsComponent, TableAction } from "../../../s25-table/generics/generic.table.actions.component";
import { WorkflowService } from "../../../workflow/service/workflow.service";
import { S25Util } from "../../../../util/s25-util";
import { GenericTableRenderHTMLComponent } from "../../../s25-table/generics/generic.table.render.html.component";
import { BudgetUtil } from "../budget.util";
import { BudgetService } from "../budget.service";
import { BudgetLabelPhrases, BudgetLabels } from "../budget.labels";

@TypeManagerDecorator("s25-ng-budget-admin-requests")
@Component({
    selector: "s25-ng-budget-admin-requests",
    template: `
        <div class="requests-tab">
            <div class="section-header">
                <h3>{{ labels.PENDING_BUDGET_REQUESTS }}</h3>
            </div>

            @if (isLoading) {
                <div class="no-requests">
                    <p>{{ labels.LOADING_PENDING_REQUESTS }}</p>
                </div>
            } @else if (error) {
                <div class="no-requests">
                    <p>{{ error }}</p>
                </div>
            } @else if (getRequests().length > 0) {
                <s25-ng-table
                    class="c-margin-left--single c-margin-right--single c-margin-top--single"
                    [dataSource]="requestsTableData"
                    [hasRefresh]="true"
                    [hasFilter]="true"
                    [columnSortable]="true"
                    [hasTotalRowCount]="true"
                    [caption]="labels.PENDING_BUDGET_REQUESTS"
                ></s25-ng-table>
            } @else {
                <div class="no-requests">
                    <p>No pending budget requests for {{ activeFiscalYear || selectedFiscalYear() }}</p>
                </div>
            }

            <!-- Request Changes Modal -->
            @if (showRequestChangesModal()) {
                <div class="modal-overlay" (click)="hideRequestChangesModal()">
                    <div class="modal-content" (click)="$event.stopPropagation()">
                        <div class="modal-header">
                            <h3>Request Changes</h3>
                            <button class="modal-close" (click)="hideRequestChangesModal()">×</button>
                        </div>
                        <div class="modal-body">
                            <label for="changesText">Please describe the changes needed:</label>
                            <textarea
                                id="changesText"
                                [(ngModel)]="requestedChangesText"
                                class="form-control"
                                rows="4"
                                placeholder="Explain what needs to be changed..."
                            ></textarea>
                        </div>
                        <div class="modal-footer">
                            <button
                                class="aw-button aw-button--primary"
                                (click)="submitRequestChanges()"
                                [disabled]="!requestedChangesText.trim()"
                            >
                                Request Changes
                            </button>
                            <button class="aw-button aw-button--outline" (click)="hideRequestChangesModal()">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        .requests-tab {
            background: var(--color--surface-bg);
            border-radius: 8px;
            box-shadow: 0 2px 8px color-mix(in srgb, var(--color--text) 12%, transparent);
            overflow: hidden;
        }

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.5rem;
            background: var(--color--surface-2-bg);
            border-bottom: 1px solid var(--color--border);
        }

        .section-header h3 {
            margin: 0;
            font-size: 1.2rem;
            color: var(--color--text);
            font-weight: 600;
        }

        .no-requests {
            text-align: center;
            padding: 3rem;
            color: var(--color--text-muted);
        }

        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: color-mix(in srgb, black 50%, transparent);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
        }

        .modal-content {
            background: var(--color--surface-bg);
            border-radius: 8px;
            min-width: 400px;
            max-width: 500px;
            max-height: 80vh;
            overflow-y: auto;
            box-shadow: 0 4px 20px color-mix(in srgb, var(--color--text) 15%, transparent);
        }

        .modal-header {
            padding: 20px;
            border-bottom: 1px solid var(--color--border);
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .modal-header h3 {
            margin: 0;
            color: var(--color--text);
        }

        .modal-close {
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: var(--color--text-muted);
            padding: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .modal-close:hover {
            color: var(--color--text);
        }

        .modal-body {
            padding: 20px;
        }

        .modal-body label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: var(--color--text-muted);
        }

        .form-control {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            font-size: 14px;
            box-sizing: border-box;
            background: var(--color--surface-bg);
            color: var(--color--text);
        }

        .form-control:focus {
            outline: none;
            border-color: var(--cn-blue);
            box-shadow: 0 0 0 2px color-mix(in srgb, var(--cn-blue) 28%, transparent);
        }

        .modal-footer {
            padding: 20px;
            border-top: 1px solid var(--color--border);
            display: flex;
            justify-content: flex-end;
            gap: 8px;
        }

        @media (max-width: 768px) {
            .section-header {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [FormsModule, S25TableModule],
})
export class BudgetAdminRequestsComponent {
    readonly labels = BudgetLabels;

    readonly pendingRequests = input<Budget.BudgetPlan[]>([]);
    readonly selectedFiscalYear = input<string>("");
    readonly requestsProcessed = output<void>();

    showRequestChangesModal = signal(false);
    requestedChangesText = "";
    isLoading = false;
    error: string | null = null;
    loadedRequests: Budget.BudgetPlan[] = [];
    activeFiscalYear = "";
    private currentPlanForRequestChanges: Budget.BudgetPlan | null = null;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    constructor() {
        effect(() => {
            this.activeFiscalYear = this.selectedFiscalYear();
            if (this.pendingRequests().length > 0) {
                return;
            }
            void this.loadPendingRequests();
        });
    }

    requestsTableData: Table.DataSource = {
        type: "paginated",
        dataSource: this.getRequestsTableRows,
        defaultPageSize: 25,
        pageSizes: [10, 25, 50, 100],
        columns: [
            {
                id: "organizationName",
                header: "Organization",
                minWidth: 180,
                cells: { text: "", className: "font-weight-medium" },
            },
            {
                id: "name",
                header: BudgetLabels.BUDGET,
                minWidth: 180,
                cells: { text: "" },
            },
            {
                id: "amount",
                header: "Amount",
                minWidth: 130,
                align: "right",
                cells: { text: "", className: "currency-amount" },
            },
            {
                id: "submissionDate",
                header: "Submitted",
                minWidth: 120,
                cells: { text: "" },
            },
            {
                id: "workflowStatus",
                header: "Status",
                minWidth: 120,
                align: "center",
                cells: { text: "" },
            },
            {
                id: "actions",
                header: "Actions",
                minWidth: 250,
                align: "center",
                sortable: false,
                cells: { text: "" },
            },
        ],
    };

    @Bind
    async getRequestsTableRows(params: Table.PaginatedQuery): Promise<Table.DataSourceResponse> {
        if (!this.pendingRequests().length) {
            await this.loadPendingRequests();
        }

        const pageSize = params.pageSize || 25;
        const currentPage = params.page || 1;
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const requests = this.getRequests();

        // Get the paginated slice of requests
        const paginatedRequests = requests.slice(startIndex, endIndex);

        const rows: Table.Row[] = paginatedRequests.map((plan) => {
            const statusInfo = BudgetUtil.getStatusInfo(plan.workflowStatus);
            const canApprove = BudgetUtil.canApprove(plan);

            return {
                id: plan.budgetPlanId,
                name: plan.name,
                data: plan,
                cells: {
                    organizationName: { text: plan.organizationName || "Unknown Organization" },
                    name: { text: plan.name || BudgetLabelPhrases.unnamedBudget },
                    amount: {
                        text: S25PricingUtil.formatCurrency(BudgetUtil.getAmount(plan), plan.currency || "USD"),
                    },
                    submissionDate: {
                        text: plan.submissionDate ? new Date(plan.submissionDate).toLocaleDateString() : "N/A",
                    },
                    workflowStatus: {
                        component: GenericTableRenderHTMLComponent,
                        inputs: {
                            text: `<span class="status-badge status-${statusInfo.class}">${statusInfo.label}</span>`,
                            isHtml: true,
                        },
                        textValue: statusInfo.label,
                    },
                    actions: {
                        component: GenericTableActionsComponent,
                        inputs: {
                            actions: this.getActions(plan, canApprove),
                        },
                    },
                },
            };
        });

        return {
            rows,
            totalRows: requests.length,
        };
    }

    private getActions(plan: Budget.BudgetPlan, canApprove: boolean): TableAction[] {
        const actions: TableAction[] = [];

        if (canApprove) {
            actions.push({
                label: "Approve",
                type: "success",
                onClick: () => this.approveBudgetPlan(plan),
                visible: true,
            });

            actions.push({
                label: "Deny",
                type: "danger",
                onClick: () => this.denyBudgetPlan(plan),
                visible: true,
            });

            actions.push({
                label: "Request Changes",
                type: "warning",
                onClick: () => this.requestChanges(plan),
                visible: true,
            });
        }

        return actions;
    }

    async approveBudgetPlan(plan: Budget.BudgetPlan): Promise<void> {
        if (!confirm(`Are you sure you want to approve "${plan.name}"?`)) {
            return;
        }

        try {
            if (!plan.todoId) {
                S25Util.showError(BudgetLabelPhrases.noTodoForBudget);
                return;
            }

            await WorkflowService.approveSubmissionFromTodo(plan.todoId);
            await this.refreshRequestsIfNeeded();
            this.requestsProcessed.emit();
            alert(BudgetLabelPhrases.budgetApprovedSuccessfully);
        } catch (error) {
            console.error("Error approving budget plan:", error);
            S25Util.showError(error, BudgetLabelPhrases.failedApproveBudget);
        }
    }

    async denyBudgetPlan(plan: Budget.BudgetPlan): Promise<void> {
        if (!confirm(`Are you sure you want to deny "${plan.name}"?`)) {
            return;
        }

        try {
            if (!plan.todoId) {
                S25Util.showError(BudgetLabelPhrases.noTodoForBudget);
                return;
            }

            await WorkflowService.denySubmissionFromTodo(plan.todoId);
            await this.refreshRequestsIfNeeded();
            this.requestsProcessed.emit();
            alert(BudgetLabelPhrases.budgetDeniedSuccessfully);
        } catch (error) {
            console.error("Error denying budget plan:", error);
            S25Util.showError(error, BudgetLabelPhrases.failedDenyBudget);
        }
    }

    requestChanges(plan: Budget.BudgetPlan): void {
        this.currentPlanForRequestChanges = plan;
        this.requestedChangesText = "";
        this.showRequestChangesModal.set(true);
    }

    hideRequestChangesModal(): void {
        this.showRequestChangesModal.set(false);
        this.currentPlanForRequestChanges = null;
        this.requestedChangesText = "";
    }

    async submitRequestChanges(): Promise<void> {
        if (!this.currentPlanForRequestChanges || !this.requestedChangesText.trim()) {
            return;
        }

        try {
            if (!this.currentPlanForRequestChanges.todoId) {
                S25Util.showError(BudgetLabelPhrases.noTodoForBudget);
                return;
            }

            await WorkflowService.requestChangesFromTodo(
                this.currentPlanForRequestChanges.todoId,
                this.requestedChangesText.trim(),
            );

            this.hideRequestChangesModal();
            await this.refreshRequestsIfNeeded();
            this.requestsProcessed.emit();
            alert("Changes requested successfully");
        } catch (error) {
            console.error("Error requesting changes:", error);
            S25Util.showError(error, "Failed to request changes");
        }
    }

    getRequests(): Budget.BudgetPlan[] {
        return this.pendingRequests().length ? this.pendingRequests() : this.loadedRequests;
    }

    private async refreshRequestsIfNeeded(): Promise<void> {
        if (!this.pendingRequests().length) {
            await this.loadPendingRequests();
        }
    }

    private async loadPendingRequests(): Promise<void> {
        this.isLoading = true;
        this.error = null;
        this.changeDetectorRef.markForCheck();

        try {
            const fiscalYearId = await this.resolveFiscalYearId();
            if (!fiscalYearId) {
                this.loadedRequests = [];
                return;
            }

            const [requests, error] = await S25Util.Maybe(BudgetService.getBudgetPlanRequests(fiscalYearId));
            if (error) {
                throw error;
            }

            this.loadedRequests = requests || [];
        } catch (error) {
            this.error = error instanceof Error ? error.message : "Failed to load pending budget requests";
            this.loadedRequests = [];
        } finally {
            this.isLoading = false;
            this.changeDetectorRef.markForCheck();
        }
    }

    private async resolveFiscalYearId(): Promise<number | null> {
        const fiscalYearName = this.selectedFiscalYear();
        if (fiscalYearName) {
            this.activeFiscalYear = fiscalYearName;
            const [fiscalYears, error] = await S25Util.Maybe(BudgetService.getFiscalYears());
            if (error) {
                throw error;
            }

            const matchedFiscalYear = (fiscalYears || []).find((fiscalYear) => fiscalYear.name === fiscalYearName);
            return matchedFiscalYear?.fiscalYearId || null;
        }

        const [currentFiscalYear, error] = await S25Util.Maybe(BudgetService.getCurrentFiscalYear());
        if (error) {
            throw error;
        }

        this.activeFiscalYear = currentFiscalYear?.name || "";
        return currentFiscalYear?.fiscalYearId || null;
    }
}
